import { Datum } from "../../../components/docs/Datum.js";
import { note, p, ul } from "../blocks.js";
import { QUOTED } from "../thresholds.js";
import type { WikiSection } from "../types.js";

/**
 * The primitives section of the ICT swing topic, lifted into its own file.
 *
 * `swing-ict.tsx` is the densest page in the wiki and the whole topic did not
 * fit under the 300-line cap. The split is by section rather than by squeezing
 * the prose, so the page reads the same and one file owns one section.
 */
export const ictPrimitives: WikiSection = {
  heading: "The four primitives",
  code: "ict_sweep_* / ict_mss_* / ict_fvg_* / ict_ob_*",
  blocks: [
    p(
      <>
        Four detectors run on the daily series, all of them sitting on one shared idea: a swing
        point is a bar whose high beats every neighbour within{" "}
        <Datum>{QUOTED.SWING_DAILY_LOOKBACK}</Datum> bars on both sides, or the mirror for a low.
        The comparison is strict, because loosening it would mark a swing point at every bar of a
        flat range.
      </>,
    ),
    ul([
      <>
        <Datum>Liquidity sweep</Datum>: a run through a pair of equal levels that closed back on the
        other side. Stops rest at equal highs and equal lows, so a bar that trades through the level
        and a next bar that closes back through it has taken that liquidity and reversed. The pair
        scan runs newest first; scanning forward would return a level from eighteen months ago,
        which is the one the current bar is least likely to be sweeping.
      </>,
      <>
        <Datum>Market structure shift</Datum>: a close through a real swing level with momentum
        behind it. Four conditions, not one: a confirmed swing point rather than the bar three back,
        the previous close on the far side of it, a confirming candle in the right direction, and
        displacement of at least <Datum>{QUOTED.SWING_DISPLACEMENT_THRESHOLD}</Datum> body over
        range. Swing points are found excluding the breaking bar, because a level that bar helped
        define is not a level it broke.
      </>,
      <>
        <Datum>Fair value gap</Datum>: a three-bar imbalance where the third bar's low clears the
        first bar's high by more than <Datum>{QUOTED.SWING_FVG_MIN_ATR}</Datum> ATR, or the mirror.
        Every three-bar window is scanned, not only the newest, because a gap that formed six
        sessions ago and is still unfilled is exactly the zone a swing entry waits for. Fill is
        tracked forward, and the zone is anchored to the middle bar.
      </>,
      <>
        <Datum>Order block</Datum>: the last bearish candle before a bullish displacement move, or
        the mirror. The move has to reach <Datum>{QUOTED.SWING_OB_MIN_MOVE_ATR}</Datum> ATR within{" "}
        <Datum>{QUOTED.SWING_OB_FORWARD_BARS}</Datum> bars, measured from the block's own high or
        low rather than its close, because the block is a zone and what matters is how far price
        travelled from it. The zone runs from the low to the body high, where the orders sat, and it
        is mitigated once a later bar trades through its far edge.
      </>,
    ]),
    p(
      <>
        The primitives fire whatever the composite does, including on a neutral weekly bias. A sweep
        is evidence either way, and the counts are what tell a quiet market apart from a ladder that
        is rejecting everything. Only the two hard gates suppress them.
      </>,
    ),
    note(
      <>
        Primitive rows carry <Datum>null</Datum> stops and targets, because a structure event is not
        a trade. Their magnitude is the event size in ATR: sweep depth past the level, and the
        distance a shift closed beyond the level it broke. Filled gaps and mitigated blocks stay in
        the detectors' lists so the composite's payload can count candidates against survivors; only
        the newest live one per direction is ever emitted.
      </>,
    ),
  ],
};
